/**
 * NotesApplication - Main application class for NotesApp_v2
 * Simplified note-taking application
 */

import * as Y from 'yjs';
import { BaseApplication } from '../../../services/baseApplication/BaseApplication';
import { AppConfig, SerializedNode } from '../../../services/baseApplication/types';
import { NOTES_APP_CONFIG, isNoteClassName } from './NotesConfig';
import { createCollaborationProvider } from '../../../services/collaboration';
import { authService } from '../../../services/auth/authService';
import { entityService } from '../../../services/entityService';
import { generateUserColor } from '../../shared/appHelpers';
import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('NOTES_APPLICATION');

export class NotesApplication extends BaseApplication {
  // ✅ collaborationProvider inherited from BaseApplication
  private entityName: string = '';
  
  constructor() {
    super();
    // Enable suggestion mode so AI writes are tracked as accept/reject suggestions
    this.suggestionMode = true;
  }
  
  /**
   * Get application configuration
   */
  protected getConfig(): AppConfig {
    return NOTES_APP_CONFIG;
  }

  protected getWebSocketRoomPrefix(): string {
    return 'note';
  }
  
  /**
   * Load note from backend
   */
  async load(entityId: string, options: { offline?: boolean } = {}): Promise<void> {
    log.log('[NotesApplication] Loading note:', entityId);
    
    const entity = await entityService.get(entityId);
    if (!entity) throw new Error(`Note not found: ${entityId}`);
    
    this.entityName = entity.name || 'Untitled Note';
    
    // Initialize (sets entityId, creates base structures)
    await this.initialize(entityId);
    
    // Create self-contained Y.Doc
    this.ydoc = new Y.Doc();
    
    // Load base64 state if present
    if (entity.skeleton?.base64 && this.ydoc) {
      try {
        this.loadBase64State(entity.skeleton.base64);
      } catch (error) {
        log.error('[NotesApplication] Failed to load Y.js state:', error);
        this.emit('loadError', { 
          error, 
          message: 'Failed to load note data. Starting with empty note.' 
        });
      }
    }
    
    // Get Y.js containers and set up save + indices
    if (this.ydoc) {
      // CRITICAL: Use 'prosemirror' fragment name to match AI write commands
      this.yxml = this.ydoc.getXmlFragment('prosemirror');
      
      // Setup auto-save BEFORE building indices so migrations get auto-saved
      this.setupAutoSave();
      
      await this.buildCoreIndices();
      
      // Migration: auto-assign IDs to existing content that lacks them
      if (this.byIdIndex.size === 0 && this.yxml && this.yxml.length > 0) {
        await this.assignMissingIds();
        await this.buildCoreIndices();
      }
    }
    
    // Setup WebSocket collaboration BEFORE initializing empty note so that existing
    // content arriving via WS sync takes precedence over a blank initialization.
    if (!options.offline && this.ydoc) {
      await this.setupCollaborationWithDoc(entityId, this.ydoc);
    }
    
    // Only initialize as empty if the fragment is still empty after WS sync settles.
    // This prevents blank-on-navigation when SERVER_IS_DURABILITY_BOUNDARY=true means
    // no base64 was written by the client but the WS room holds the actual content.
    if (this.yxml) {
      if (this.yxml.length === 0 && !entity.skeleton?.base64) {
        this.initializeEmptyNote();
      } else if (this.yxml.length === 0 && entity.skeleton?.base64) {
        log.error('[NotesApplication] Fragment empty even though base64 exists — this is a bug');
      }
    }
    
    log.log('[NotesApplication] Note load complete:', entityId);
  }
  
  /**
   * Migration: Auto-assign IDs to existing content without IDs
   */
  private async assignMissingIds(): Promise<void> {
    if (!this.yxml || !this.ydoc) return;
    
    let assignedCount = 0;
    
    this.ydoc.transact(() => {
      for (let i = 0; i < this.yxml!.length; i++) {
        const child = this.yxml!.get(i);
        
        if (child instanceof Y.XmlElement) {
          const id = child.getAttribute('id');
          
          if (!id || id === 'undefined' || id === 'null') {
            const newId = this.generateId();
            child.setAttribute('id', newId);
            assignedCount++;
          }
        }
      }
    }, 'notes-migration');
    
    if (assignedCount > 0) {
      log.log(`[NotesApplication] Migration: assigned IDs to ${assignedCount} node(s)`);
    }
  }
  
  /**
   * Set up WebSocket collaboration with proper cleanup to prevent memory leaks
   */
  private async setupCollaborationWithDoc(entityId: string, ydoc: Y.Doc): Promise<void> {
    const authUser = authService.getCurrentUser();
    if (!authUser) {
      throw new Error('Not authenticated');
    }
    
    const currentUser = {
      id: authUser.id,
      name: authUser.name || authUser.email || 'User',
      color: generateUserColor(authUser.id)
    };
    
    this.collaborationProvider = createCollaborationProvider({
      documentId: `note:${entityId}`,
      user: currentUser,
      websocketUrl: this.getWebSocketUrl(),
      authToken: '',
      offline: false,
      doc: ydoc,
    });
    
    this.collaborationProvider.connect();
    
    await new Promise<void>((resolve) => {
      let timeoutId: NodeJS.Timeout | null = null;
      let syncedHandler: (() => void) | null = null;
      let errorHandler: ((error: Error) => void) | null = null;
      
      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (syncedHandler && this.collaborationProvider) {
          this.collaborationProvider.off('synced', syncedHandler);
          syncedHandler = null;
        }
        if (errorHandler && this.collaborationProvider) {
          this.collaborationProvider.off('error', errorHandler);
          errorHandler = null;
        }
      };
      
      timeoutId = setTimeout(() => {
        log.log('[NotesApplication] WebSocket sync timeout (5s), proceeding');
        cleanup();
        resolve();
      }, 5000);
      
      syncedHandler = () => {
        cleanup();
        resolve();
      };
      
      errorHandler = (error: Error) => {
        log.warn('[NotesApplication] WebSocket sync error:', error);
        cleanup();
        resolve();
      };
      
      this.collaborationProvider!.on('synced', syncedHandler);
      this.collaborationProvider!.on('error', errorHandler);
    });
  }
  
  /**
   * Initialize empty note with a blank paragraph
   */
  private initializeEmptyNote(): void {
    if (!this.yxml) return;
    
    const paragraph = new Y.XmlElement('paragraph');
    paragraph.setAttribute('id', this.generateId());
    paragraph.setAttribute('className', '');
    paragraph.insert(0, [new Y.XmlText()]);
    this.yxml.insert(0, [paragraph]);
  }
  
  /**
   * Notes-specific: Get all tags used in the note
   */
  public getAllTags(): string[] {
    const tags = new Set<string>();
    
    for (const node of this.byIdIndex.values()) {
      if (node.attrs?.className === 'note-tag' && node.attrs?.semantic?.tag) {
        tags.add(node.attrs.semantic.tag);
      }
    }

    return Array.from(tags).sort();
  }

  /**
   * Notes-specific: Get all checklist items
   */
  public getAllChecklistItems(): Array<{ node: SerializedNode; checked: boolean }> {
    const items: Array<{ node: SerializedNode; checked: boolean }> = [];

    for (const node of this.byIdIndex.values()) {
      if (node.attrs?.className === 'note-checklist') {
        items.push({
          node: node as any,
          checked: Boolean(node.attrs.semantic?.checked)
        });
      }
    }

    return items;
  }

  /**
   * Notes-specific: Get note statistics
   */
  public getNoteStats() {
    const allNodes = Array.from(this.byIdIndex.values());
    const checklistItems = this.getAllChecklistItems();
    
    return {
      totalElements: allNodes.length,
      textElements: this.getNodesByClassName('note-text').length,
      lists: this.getNodesByClassName('note-bullet-list').length + 
             this.getNodesByClassName('note-numbered-list').length,
      checklists: checklistItems.length,
      completedTasks: checklistItems.filter(item => item.checked).length,
      tags: this.getAllTags().length,
      images: this.getNodesByClassName('note-image').length,
      codeBlocks: this.getNodesByClassName('note-code').length,
    };
  }

  /**
   * Notes-specific: Export to plain text
   */
  public exportToPlainText(): string {
    const allNodes = Array.from(this.byIdIndex.values()).filter(node => 
      isNoteClassName(node.attrs?.className || '')
    );

    const lines: string[] = [];

    for (const node of allNodes) {
      const text = this.extractTextFromNode(node as any);
      if (text) {
        lines.push(text);
        lines.push('');
      }
    }

    return lines.join('\n');
  }

  /**
   * Notes-specific: Export to markdown
   */
  public exportToMarkdown(): string {
    const allNodes = Array.from(this.byIdIndex.values()).filter(node => 
      isNoteClassName(node.attrs?.className || '')
    );

    const lines: string[] = [];

    for (const node of allNodes) {
      const className = node.attrs?.className;
      const text = this.extractTextFromNode(node as any);
      
      if (!text) continue;

      if (className === 'note-title') {
        lines.push(`# ${text}`);
      } else if (className === 'note-text') {
        lines.push(text);
      } else if (className === 'note-quote') {
        lines.push(`> ${text}`);
      } else if (className === 'note-code') {
        lines.push('```');
        lines.push(text);
        lines.push('```');
      } else if (className === 'note-checklist') {
        const checked = node.attrs?.semantic?.checked ? 'x' : ' ';
        lines.push(`- [${checked}] ${text}`);
      } else if (className === 'note-bullet-list') {
        lines.push(`- ${text}`);
      } else if (className === 'note-numbered-list') {
        lines.push(`1. ${text}`);
      } else if (className === 'note-divider') {
        lines.push('---');
      } else {
        lines.push(text);
      }

      lines.push('');
    }

    return lines.join('\n');
  }

  /**
   * Helper: Extract text from node
   */
  private extractTextFromNode(node: SerializedNode): string {
    if (node.text) return node.text;
    if (node.content) {
      return node.content.map(child => this.extractTextFromNode(child)).join(' ');
    }
    return '';
  }

  /**
   * Get the current entity name (title)
   */
  public getEntityName(): string {
    return this.entityName;
  }

  /**
   * Internal setter for entity name (doesn't call backend)
   * Used when external update already happened (e.g., sidebar rename)
   */
  public _setEntityName(newName: string): void {
    this.entityName = newName;
  }

  /**
   * Return the Y.Doc instance for this note.
   * Used by the content-reload listener in NotesApp_v2.tsx to merge
   * freshly fetched base64 into the live Y.Doc after an agent write.
   */
  public getYDoc(): Y.Doc | null {
    return this.ydoc ?? null;
  }

  /**
   * Update the entity name (title)
   * Uses entityStore.actions to ensure events are emitted
   */
  public async updateEntityName(newName: string): Promise<void> {
    const trimmedName = newName.trim();
    const finalName = trimmedName || 'Untitled Note';
    
    this.entityName = finalName;
    
    if (!this.entityId) {
      log.warn('[NotesApplication] Cannot update entity name — no entityId');
      return;
    }
    
    // Use entityStore.actions.updateEntity to emit events so sidebar and other
    // components get notified of the change
    const { default: useEntityStore } = await import('../../../stores/entityStore');
    await useEntityStore.getState().actions.updateEntity(this.entityId, { 
      name: finalName 
    });
  }
}
